/**
 * DDPacketPanel — Investor-ready exports + magic-link shares.
 *
 * Sits below the roadmap on `/dashboard/startups/{id}/roadmap`. Three jobs:
 *   1. Download buttons for the Tear Sheet and full DD Packet PDFs.
 *   2. "Share with investor" dialog → mints a magic link (token + expiry)
 *      and lets the founder copy / send it.
 *   3. Active shares list — investor name, kind, view count, last view,
 *      revoke button. Every view is also surfaced in the platform's
 *      audit log (`dd_share_viewed`).
 *
 * Backend:
 *   GET    /api/scaling-lean/roadmap/{startup_id}/tear-sheet.pdf
 *   GET    /api/scaling-lean/roadmap/{startup_id}/dd-packet.pdf
 *   GET    /api/scaling-lean/roadmap/{startup_id}/dd-shares
 *   POST   /api/scaling-lean/roadmap/{startup_id}/dd-shares
 *   DELETE /api/scaling-lean/roadmap/{startup_id}/dd-shares/{share_id}
 */
import React, { useEffect, useState } from 'react';
import {
  FileDown, Sparkles, Share2, Loader2, X, Copy, Check, Eye, Trash2, Mail, Clock, ShieldCheck, Send, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the startup-bound shares load. */

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const statusPill = (s) => {
  const colour = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300',
    expired: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300',
    revoked: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300',
  }[s] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded border ${colour}`}>
      {s}
    </span>
  );
};

export default function DDPacketPanel({ startupId, startupName }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/scaling-lean/roadmap/${startupId}/dd-shares`);
      setShares(data || []);
    } catch (e) {
      // Non-fatal — first call before any share exists may 404 from old
      // versions; log so it's visible if it starts happening.
      console.warn('DDPacketPanel: shares load failed', e?.response?.status, e?.message);
    }
  };

  // Standard mount-effect: refresh shares when startup changes.
  // eslint-disable-next-line
  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [startupId]);

  /**
   * Download a PDF directly to disk by hitting the backend endpoint and
   * triggering a browser save. We use blob + object URL so the auth
   * cookie flows correctly (a plain <a download> would bypass it on
   * cross-origin in some configs).
   */
  const downloadPdf = async (kind) => {
    setDownloading(kind);
    try {
      const path =
        kind === 'tear_sheet'
          ? `/scaling-lean/roadmap/${startupId}/tear-sheet.pdf`
          : `/scaling-lean/roadmap/${startupId}/dd-packet.pdf`;
      const resp = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(startupName || 'vigilant').replace(/\s+/g, '_')}_${kind}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate PDF.');
    } finally {
      setDownloading(null);
    }
  };

  const revoke = async (share) => {
    if (!window.confirm(`Revoke link for ${share.investor_name}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/scaling-lean/roadmap/${startupId}/dd-shares/${share.share_id}`);
      toast.success(`Link for ${share.investor_name} revoked.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke.');
    }
  };

  return (
    <section className="ve-card p-6 mt-8" data-testid="dd-packet-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-1.5">
            <ShieldCheck size={11} /> Investor-Ready Exports
          </div>
          <h3 className="display text-xl font-bold mt-1">Boardroom-grade deliverables, one click.</h3>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-1 max-w-2xl">
            Every saved AI artefact compiles into a branded PDF. Send a private magic-link to an investor — no login required on their side, full view tracking on yours.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <button
          type="button"
          onClick={() => downloadPdf('tear_sheet')}
          disabled={downloading !== null}
          data-testid="dd-download-tear-sheet"
          className="ve-btn-secondary text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {downloading === 'tear_sheet'
            ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
            : <><FileDown size={13} /> Tear sheet (1 page)</>}
        </button>
        <button
          type="button"
          onClick={() => downloadPdf('dd_packet')}
          disabled={downloading !== null}
          data-testid="dd-download-dd-packet"
          className="ve-btn-primary text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {downloading === 'dd_packet'
            ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
            : <><Sparkles size={13} /> Full DD packet</>}
        </button>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="dd-share-create"
          className="text-sm inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border-2 border-[#8B3FC5] text-[#5E1F8B] dark:text-[#D4AF37] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a]/40 font-bold transition"
        >
          <Share2 size={13} /> Share with investor
        </button>
      </div>

      {/* Active shares list */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-2">
          Magic links — view tracking
        </div>
        {loading && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && shares.length === 0 && (
          <p className="text-xs text-slate-500 dark:text-white/55 italic">
            No shares yet. Create one to send the DD packet to a specific investor and track when they open it.
          </p>
        )}
        {!loading && shares.length > 0 && (
          <ul className="space-y-2" data-testid="dd-shares-list">
            {shares.map((s) => (
              <li
                key={s.share_id}
                data-testid={`dd-share-${s.share_id}`}
                className="rounded-lg border border-slate-200 dark:border-white/10 p-3 flex items-start justify-between gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">{s.investor_name}</span>
                    {statusPill(s.status)}
                    <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                      {s.kind === 'tear_sheet' ? 'TEAR SHEET' : 'DD PACKET'}
                    </span>
                  </div>
                  {s.investor_email && (
                    <div className="text-xs text-slate-500 dark:text-white/55 mt-0.5">
                      <Mail size={10} className="inline mr-1" /> {s.investor_email}
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40 font-bold mt-1.5">
                    Created {fmtDate(s.created_at)} · Expires {fmtDate(s.expires_at)}
                  </div>
                  {s.note && (
                    <p className="text-[11px] text-slate-500 dark:text-white/55 italic mt-1">&ldquo;{s.note}&rdquo;</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#5E1F8B] dark:text-[#D4AF37]">{s.views || 0}</div>
                    <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                      <Eye size={9} className="inline mr-1" /> views
                    </div>
                  </div>
                  {s.last_viewed_at && (
                    <div className="text-[10px] text-slate-500 dark:text-white/55 max-w-[10rem] text-right">
                      <Clock size={10} className="inline mr-1" /> Last {fmtDate(s.last_viewed_at)}
                    </div>
                  )}
                  {s.status === 'active' && (
                    <button
                      onClick={() => revoke(s)}
                      data-testid={`dd-share-revoke-${s.share_id}`}
                      title="Revoke this link immediately"
                      className="h-8 w-8 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900/40 dark:hover:bg-rose-900/20 flex items-center justify-center transition"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create-share dialog */}
      {createOpen && (
        <CreateShareDialog
          startupId={startupId}
          onClose={() => setCreateOpen(false)}
          onCreated={(token, share) => {
            refresh();
            // Keep the dialog open showing the magic link — user closes manually.
            return { token, share };
          }}
        />
      )}
    </section>
  );
}


function CreateShareDialog({ startupId, onClose, onCreated }) {
  const [form, setForm] = useState({
    investor_name: '',
    investor_email: '',
    note: '',
    ttl_days: 14,
    kind: 'dd_packet',
    send_email: false,
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!form.investor_name.trim()) {
      toast.error('Investor name is required.');
      return;
    }
    if (form.send_email && !form.investor_email.trim()) {
      toast.error('Investor email is required to send by email.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/scaling-lean/roadmap/${startupId}/dd-shares`, {
        ...form,
        // Pass the founder's branded origin so the email body links to
        // the right host (preview vs prod). Backend falls back if missing.
        public_origin: window.location.origin,
      });
      setCreated(data);
      onCreated(data.token, data);
      const es = data.email_status || {};
      if (es.requested && es.sent) {
        toast.success(`Magic link minted & emailed to ${form.investor_email}.`);
      } else if (es.requested && !es.sent) {
        toast.warning('Link minted, but email delivery failed — copy the URL below and send manually.');
      } else {
        toast.success('Magic link minted.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create share.');
    } finally {
      setBusy(false);
    }
  };

  // The frontend route for an investor to land on. We use REACT_APP_BACKEND_URL
  // as the public base; the actual share landing page lives at /share/dd/{token}.
  const shareUrl =
    created
      ? `${window.location.origin}/share/dd/${created.token}`
      : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="dd-share-dialog"
    >
      <div
        className="relative max-w-lg w-full rounded-2xl bg-white dark:bg-[#0d0d0d] shadow-2xl border border-[#8B3FC5]/40"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="dd-share-dialog-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 flex items-center justify-center"
        >
          <X size={16} />
        </button>
        <div className="p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#D4AF37] flex items-center gap-1.5">
            <Share2 size={11} /> Share with investor
          </div>
          <h2 className="display text-2xl font-bold mt-1 leading-tight">Mint a private link.</h2>
          <p className="text-sm text-slate-500 dark:text-white/55 mt-1">
            They click → see the packet. You see exactly when, and how often.
          </p>

          {!created ? (
            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
                  Investor name <span className="text-rose-500">*</span>
                </label>
                <input
                  value={form.investor_name}
                  onChange={(e) => setForm((f) => ({ ...f, investor_name: e.target.value }))}
                  placeholder="e.g. Acme Capital — Sarah Khan"
                  data-testid="dd-share-investor-name"
                  className="w-full rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                  maxLength={120}
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={form.investor_email}
                  onChange={(e) => setForm((f) => ({ ...f, investor_email: e.target.value }))}
                  placeholder="sarah@acme.vc"
                  data-testid="dd-share-investor-email"
                  className="w-full rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                  maxLength={200}
                />
                {/* Zero-friction email delivery toggle — only enabled when
                    the founder has typed an investor email. */}
                <label
                  className={`mt-2 flex items-start gap-2 text-xs cursor-pointer select-none ${form.investor_email.trim() ? '' : 'opacity-50 cursor-not-allowed'}`}
                  data-testid="dd-share-send-email-label"
                >
                  <input
                    type="checkbox"
                    checked={form.send_email}
                    onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))}
                    disabled={!form.investor_email.trim()}
                    data-testid="dd-share-send-email-toggle"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#8B3FC5] focus:ring-[#8B3FC5]"
                  />
                  <span className="text-slate-600 dark:text-white/70">
                    <Send size={11} className="inline mr-1 text-[#5E1F8B] dark:text-[#D4AF37]" />
                    Send link directly to investor via email
                    <span className="block text-[10px] text-slate-400 dark:text-white/40 mt-0.5">
                      Luxury branded template · Sender appears as <strong>Vigilant Entities · Sign-in</strong>
                    </span>
                  </span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
                    Kind
                  </label>
                  <select
                    value={form.kind}
                    onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                    data-testid="dd-share-kind"
                    className="w-full rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                  >
                    <option value="dd_packet">Full DD packet</option>
                    <option value="tear_sheet">1-page tear sheet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
                    Expires in (days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={form.ttl_days}
                    onChange={(e) => setForm((f) => ({ ...f, ttl_days: parseInt(e.target.value || '14', 10) }))}
                    data-testid="dd-share-ttl"
                    className="w-full rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
                  Private note (optional)
                </label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Context only you see — 'sent for Q3 partner call'"
                  data-testid="dd-share-note"
                  className="w-full min-h-[70px] rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] resize-y"
                  maxLength={400}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="text-xs font-bold text-slate-600 dark:text-white/70 hover:text-[#5E1F8B] px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !form.investor_name.trim() || (form.send_email && !form.investor_email.trim())}
                  data-testid="dd-share-submit"
                  className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {busy
                    ? <><Loader2 size={13} className="animate-spin" /> {form.send_email ? 'Minting & sending…' : 'Minting…'}</>
                    : form.send_email
                      ? <><Send size={13} /> Mint &amp; send</>
                      : <><Sparkles size={13} /> Mint link</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-5">
              {/* Email delivery status (only shown if the founder ticked "Send by email") */}
              {created.email_status?.requested && (
                <div
                  data-testid="dd-share-email-status"
                  className={`mb-3 rounded-lg border p-3 flex items-start gap-2 ${
                    created.email_status.sent
                      ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700'
                      : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700'
                  }`}
                >
                  {created.email_status.sent
                    ? <Send size={13} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                    : <AlertTriangle size={13} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />}
                  <div className="text-xs leading-relaxed">
                    {created.email_status.sent ? (
                      <>
                        <strong className="text-emerald-800 dark:text-emerald-200">Email delivered.</strong>{' '}
                        Sent to <strong className="font-mono">{created.investor_email}</strong>. They&rsquo;ll see a luxury-branded message with the open-packet CTA.
                      </>
                    ) : (
                      <>
                        <strong className="text-amber-800 dark:text-amber-200">Email delivery failed.</strong>{' '}
                        {created.email_status.reason || 'Copy the URL below and send it manually.'}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700 p-4">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700 dark:text-emerald-300">
                  <Check size={11} /> Link ready
                </div>
                <p className="text-sm text-slate-700 dark:text-white/80 mt-1.5">
                  Copy this URL and send it to <strong>{created.investor_name}</strong> via whichever channel you prefer. The link is single-recipient and tracked.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    data-testid="dd-share-url"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#8B3FC5]"
                  />
                  <button
                    onClick={copy}
                    data-testid="dd-share-copy"
                    className="ve-btn-primary text-xs inline-flex items-center gap-1"
                  >
                    {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                  </button>
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/55 font-bold">
                  Expires {fmtDate(created.expires_at)} · Revocable anytime from the shares list.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="dd-share-done"
                  className="ve-btn-primary text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
